/**
 * Hvor gikk kapasiteten? (VIB-2990). Fordeler førte timer på kategoriene
 * i teamets faktureringsgrad-widget og finner ukedager uten føring.
 *
 * Kategoriene speiler hva som kan bli fakturerbart etter KPI-SOT
 * «Faktureringsgrad»: timer på godkjente oppgaver (og medgått reisetid og
 * timeprosjekter, som teller direkte), timer på oppgaver som ikke er
 * godkjent ennå, timer på kundeprosjekt uten egen oppgave, og interne
 * prosjekter. Selve graden regnes fortsatt av billing-rates-calc.
 */
import { osloDateParts } from "./oslo-time";

export type CapacityCategory = "godkjent" | "venter" | "uten" | "internt";
export const CAPACITY_CATEGORIES: readonly CapacityCategory[] = ["godkjent", "venter", "uten", "internt"];

export interface BreakdownEntry {
  user_id: string;
  project_id: string | null;
  budget_line_id: string | null;
  assignment_id: string | null;
  start_date: string;
  duration_seconds: number | null;
}

export interface BreakdownProject {
  id: string;
  name: string;
  is_internal: boolean | null;
  project_type: string | null;
}

export interface BreakdownAssignment {
  id: string;
  user_id: string;
  budget_line_id: string;
  task_status: string;
  label: string | null;
  lineDescription: string | null;
  templateId: string | null;
}

export interface BreakdownContext {
  projects: Map<string, BreakdownProject>;
  assignments: Map<string, BreakdownAssignment>;
  /** `${user_id}:${budget_line_id}` → oppgaven, for føringer uten assignment_id. */
  assignmentByUserLine: Map<string, BreakdownAssignment>;
  /** Mal-id for «Reisetid (medgått)», som teller direkte. */
  reisetidTemplateId: string | null;
  /** budget_line_id → mal-id, for reisetid ført uten egen oppgave. */
  lineTemplate: Map<string, string | null>;
}

export interface ClassifiedEntry {
  userId: string;
  projectId: string | null;
  /** Oslo-dato YYYY-MM-DD. */
  date: string;
  month: number;
  hours: number;
  category: CapacityCategory;
  assignment: BreakdownAssignment | null;
}

/** Kategori for én føring. */
export function classifyEntry(entry: BreakdownEntry, ctx: BreakdownContext): ClassifiedEntry["category"] {
  const project = entry.project_id ? ctx.projects.get(entry.project_id) : undefined;
  if (project?.is_internal) return "internt";
  if (project?.project_type === "hourly") return "godkjent";
  const lineTemplate = entry.budget_line_id ? ctx.lineTemplate.get(entry.budget_line_id) : null;
  if (ctx.reisetidTemplateId && lineTemplate === ctx.reisetidTemplateId) return "godkjent";
  const assignment = findAssignment(entry, ctx);
  if (!assignment) return "uten";
  return assignment.task_status === "approved" ? "godkjent" : "venter";
}

function findAssignment(entry: BreakdownEntry, ctx: BreakdownContext): BreakdownAssignment | null {
  if (entry.assignment_id) return ctx.assignments.get(entry.assignment_id) ?? null;
  if (entry.budget_line_id) return ctx.assignmentByUserLine.get(`${entry.user_id}:${entry.budget_line_id}`) ?? null;
  return null;
}

/** Klassifiserer føringene i `year`, med Oslo-måned. */
export function classifyEntries(entries: BreakdownEntry[], ctx: BreakdownContext, year: number): ClassifiedEntry[] {
  const out: ClassifiedEntry[] = [];
  for (const e of entries) {
    const { year: y, month } = osloDateParts(e.start_date);
    if (y !== year) continue;
    out.push({
      userId: e.user_id,
      projectId: e.project_id,
      date: osloIsoDate(e.start_date),
      month,
      hours: Number(e.duration_seconds ?? 0) / 3600,
      category: classifyEntry(e, ctx),
      assignment: findAssignment(e, ctx),
    });
  }
  return out;
}

export type CategoryHours = Record<CapacityCategory, number>;
const emptyHours = (): CategoryHours => ({ godkjent: 0, venter: 0, uten: 0, internt: 0 });

/** Timer per kategori per bruker og måned: `${userId}:${month}` → timer. */
export function hoursByUserMonth(classified: ClassifiedEntry[]): Map<string, CategoryHours> {
  const map = new Map<string, CategoryHours>();
  for (const c of classified) {
    const key = `${c.userId}:${c.month}`;
    const row = map.get(key) ?? emptyHours();
    row[c.category] += c.hours;
    map.set(key, row);
  }
  return map;
}

export interface CapacitySlice extends CategoryHours {
  capacity: number;
  logged: number;
  /** Kapasitet uten førte timer, summert per bruker og måned. */
  under: number;
  /** Timer ført utover kapasiteten, summert per bruker og måned. */
  over: number;
}

/**
 * Summerer kategoriene og kapasiteten for utvalgte brukere og måneder.
 * «Ikke ført» regnes per bruker og måned, så den som fører mye én måned
 * ikke skjuler en annens tomme måned.
 */
export function capacitySlice(
  byUserMonth: Map<string, CategoryHours>,
  capacityOf: (userId: string, month: number) => number,
  userIds: string[],
  months: number[],
): CapacitySlice {
  const slice: CapacitySlice = { ...emptyHours(), capacity: 0, logged: 0, under: 0, over: 0 };
  for (const userId of userIds) {
    for (const month of months) {
      const h = byUserMonth.get(`${userId}:${month}`) ?? emptyHours();
      const cap = capacityOf(userId, month);
      const logged = h.godkjent + h.venter + h.uten + h.internt;
      for (const k of CAPACITY_CATEGORIES) slice[k] += h[k];
      slice.capacity += cap;
      slice.logged += logged;
      if (cap > logged) slice.under += cap - logged;
      else slice.over += logged - cap;
    }
  }
  return slice;
}

/** Summerer timer per nøkkel, sortert synkende. */
export function sumBy<T>(items: T[], keyOf: (item: T) => string | null, hoursOf: (item: T) => number) {
  const map = new Map<string, number>();
  for (const item of items) {
    const key = keyOf(item);
    if (key == null) continue;
    map.set(key, (map.get(key) ?? 0) + hoursOf(item));
  }
  return [...map.entries()].sort((a, b) => b[1] - a[1]);
}

const isoDate = (y: number, m: number, d: number) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/**
 * Ukedager i måneden uten noen føring, som ikke er helligdag eller dekket av
 * fravær. Et deldagsfravær (`hours` satt) dekker dagen det gjelder. Bare
 * dager før `today` telles.
 */
export function missingWeekdays(args: {
  year: number;
  month: number;
  loggedDates: Set<string>;
  holidays: Set<string>;
  absences: Array<{ start_date: string; end_date: string; status: string | null }>;
  today: string;
}): string[] {
  const { year, month, loggedDates, holidays, absences, today } = args;
  const days: string[] = [];
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  for (let d = 1; d <= lastDay; d++) {
    const iso = isoDate(year, month, d);
    if (iso >= today) break;
    const weekday = new Date(Date.UTC(year, month - 1, d)).getUTCDay();
    if (weekday === 0 || weekday === 6) continue;
    if (holidays.has(iso) || loggedDates.has(iso)) continue;
    const absent = absences.some(
      (a) => a.status !== "rejected" && a.start_date.slice(0, 10) <= iso && a.end_date.slice(0, 10) >= iso,
    );
    if (!absent) days.push(iso);
  }
  return days;
}

export type DayKind = "fort" | "mangler" | "fravaer" | "delvis" | "helligdag";

export interface DayRow {
  date: string;
  /** 0 = søndag, som Date.getUTCDay. */
  weekday: number;
  kind: DayKind;
  hours: number;
  /** Timer deldagsfravær, bare for `delvis`. */
  absenceHours: number | null;
}

/**
 * Hver dag i måneden før `today` for én ansatt (VIB-2990): alle ukedager, og
 * helgedager med føring. «Mangler» følger samme regel som `missingWeekdays`:
 * ukedag uten føring, helligdag eller ikke avvist fravær.
 */
export function dayRows(args: {
  year: number;
  month: number;
  hoursByDate: Map<string, number>;
  holidays: Set<string>;
  absences: Array<{ start_date: string; end_date: string; status: string | null; hours?: number | null }>;
  today: string;
}): DayRow[] {
  const { year, month, hoursByDate, holidays, absences, today } = args;
  const rows: DayRow[] = [];
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  for (let d = 1; d <= lastDay; d++) {
    const date = isoDate(year, month, d);
    if (date >= today) break;
    const weekday = new Date(Date.UTC(year, month - 1, d)).getUTCDay();
    const hours = hoursByDate.get(date) ?? 0;
    const weekend = weekday === 0 || weekday === 6;
    if (weekend && hours === 0) continue;
    const absence = absences.find(
      (a) => a.status !== "rejected" && a.start_date.slice(0, 10) <= date && a.end_date.slice(0, 10) >= date,
    );
    // `hours` på fravær er en deldagsmarkør og gjelder bare startdagen.
    const partial = absence?.hours != null && absence.start_date.slice(0, 10) === date ? Number(absence.hours) : null;
    let kind: DayKind;
    if (holidays.has(date)) kind = "helligdag";
    else if (absence && partial == null) kind = "fravaer";
    else if (partial != null) kind = "delvis";
    else if (hours > 0 || weekend) kind = "fort";
    else kind = "mangler";
    rows.push({ date, weekday, kind, hours, absenceHours: partial });
  }
  return rows;
}

/** Oslo-dato (YYYY-MM-DD) for en føring. */
export function osloIsoDate(instant: string): string {
  const { year, month, day } = osloDateParts(instant);
  return isoDate(year, month, day);
}

export type CapacitySegment = CapacityCategory | "ikke";

/** Farger og tekster for kapasitetsfordelingen (VIB-2990). */
export const SEGMENTS: Array<{ key: CapacitySegment; label: string; short: string; hint: string; color: string }> = [
  { key: "godkjent", label: "Godkjente oppgaver", short: "Godkjent", hint: "Teller i faktureringsgraden", color: "#00AF68" },
  { key: "venter", label: "Pågår eller venter på godkjenning", short: "Pågår", hint: "Teller når oppgaven godkjennes", color: "#50BDD8" },
  { key: "uten", label: "Kundeprosjekt uten oppgave", short: "Uten oppgave", hint: "Teller aldri", color: "#FF8888" },
  { key: "internt", label: "Interne prosjekter", short: "Internt", hint: "Kontor, tilbud, møter, egne prosjekter", color: "#8A97A3" },
  { key: "ikke", label: "Ikke ført", short: "Ikke ført", hint: "Kapasitet uten førte timer", color: "repeating-linear-gradient(135deg,#E7ECEC 0 5px,#F6F8F8 5px 9px)" },
];

/** Netto kapasitet uten føring i utvalget. */
export const unlogged = (s: CapacitySlice) => Math.max(0, s.capacity - s.logged);
export const segmentHours = (s: CapacitySlice, key: CapacitySegment) => (key === "ikke" ? unlogged(s) : s[key]);
export const shareOf = (s: CapacitySlice, key: CapacitySegment) =>
  s.capacity > 0 ? Math.round((segmentHours(s, key) / s.capacity) * 100) : 0;
